import { useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSubscriptionState, type SubscriptionState } from '@/hooks/useSubscriptionState';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useWithdrawableContracts } from '@/hooks/useWithdrawableContracts';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { LifecycleHighlightBox } from '@/components/patterns/LifecycleHighlightBox';
import { CancelConfirmModal } from '@/components/account/CancelConfirmModal';
import {
  createPortalSession,
  reactivateSubscription,
  discardScheduledChange,
} from '@/features/subscription/api';
import {
  deriveTierCardState,
  type TierCardState,
} from '@/features/subscription/derive-tier-card-state';
import { pickReserveLine } from '@/features/subscription/pick-reserve-line';
import type { PublicPlan } from '@/features/subscription/plans-api';
import { ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';

type BrowseInterval = 'yearly' | 'monthly';

/**
 * Abo-Verwaltung (A-02) — `/account/subscription`.
 *
 * Sönkes mockup `dreamable_ch2_account_overview_and_subpages.html`
 * (lines 62-81). Per Batch 9A scope decision, A-02 in this batch
 * focuses on the existing-subscriber management surface ONLY:
 * current-plan summary card with lifecycle states inline + "Abo
 * verwalten bei Stripe" CTA + "Abo kündigen" link → CancelConfirmModal.
 *
 * Out of scope for Batch 9A (deferred to Batch 11 Checkout):
 *   - Other-plans list (Entdecker / Abenteurer / Geschichten-Profi
 *     pricing cards)
 *   - Yearly/monthly billing-interval toggle
 *   - One-off package purchases
 *
 * `SubscriptionSummaryCard` is route-private here (single consumer).
 * It absorbs the lifecycle visual treatment (warn-box / err-box) per
 * the dreamable_ch2_account_states_modals.html mockup directly rather
 * than going through PlanStatusCard's tone variant — A-02's card is
 * richer (plan name + price + progress bar + usage line) than what
 * PlanStatusCard surfaces.
 */
export function SubscriptionRoute() {
  const navigate = useNavigate();
  const { state, refetch } = useSubscriptionState({ refetchOnMount: true });

  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Bug PWA-A Bug-B: in-flight guard prevents the reactivate double-fire
  // (button has no native debounce) that produced the false-error banner.
  const [isReactivating, setIsReactivating] = useState(false);
  // D1: reentry guard for the in-app "Plan-Wechsel rückgängig machen" discard.
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Subscription-Area Polish workstream Sub-Phase D — out-of-credits CTA
  // on the SummaryCard scrolls down to the BrowseMode tier-cards section
  // (already rendered below in this same route). useRef + scrollIntoView
  // keeps the action SPA-internal — no URL change, no router involvement.
  const browseModeRef = useRef<HTMLDivElement>(null);
  const handleScrollToBrowseMode = () => {
    browseModeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleManageStripe = async () => {
    setActionError(null);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.portalSessionFailed);
      }
    }
  };

  const handleCancelConfirm = async () => {
    setActionError(null);
    try {
      const { url } = await createPortalSession('subscription_cancel');
      window.location.href = url;
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.portalSessionFailed);
      }
    }
  };

  const handleReactivate = async () => {
    if (isReactivating) return;
    setActionError(null);
    setIsReactivating(true);
    try {
      await reactivateSubscription();
      // Webhook syncs DB asynchronously. Refetch at 500ms, then again at
      // 2000ms as a safety net for slow webhooks (Bug PWA-A polish — fixes
      // the refresh-race where a single 500ms refetch could fire before the
      // DB was updated).
      window.setTimeout(() => {
        void refetch();
      }, 500);
      window.setTimeout(() => {
        void refetch();
      }, 2000);
    } catch (err) {
      console.error(err);
      // Bug PWA-A Bug-B: a 400 subscription_not_pending_cancel means the
      // reactivation already took effect server-side (double-fire race or a
      // stale CTA rendered from cached state). Treat as success — refetch the
      // synced state, suppress the red banner.
      if (
        err instanceof ApiError &&
        err.httpStatus === 400 &&
        err.errorCode === 'subscription_not_pending_cancel'
      ) {
        // Same double-refetch race fix as the success path above.
        window.setTimeout(() => {
          void refetch();
        }, 500);
        window.setTimeout(() => {
          void refetch();
        }, 2000);
        return;
      }
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.reactivateFailed);
      }
    } finally {
      setIsReactivating(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setActionError(null);
    try {
      const { url } = await createPortalSession('payment_method_update');
      window.location.href = url;
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.portalSessionFailed);
      }
    }
  };

  // D1: in-app discard of a scheduled plan change — wired to the
  // "Plan-Wechsel rückgängig machen" banner CTA. Mirrors handleReactivate.
  const handleDiscardScheduledChange = async () => {
    if (isDiscarding) return;
    setActionError(null);
    setIsDiscarding(true);
    try {
      await discardScheduledChange();
      window.setTimeout(() => {
        void refetch();
      }, 500);
      window.setTimeout(() => {
        void refetch();
      }, 2000);
    } catch (err) {
      console.error(err);
      // 404 subscription_no_scheduled_change / 409 subscription_schedule_
      // already_terminated → the scheduled change is already gone; treat as
      // success, refetch, suppress the banner.
      if (
        err instanceof ApiError &&
        ((err.httpStatus === 404 && err.errorCode === 'subscription_no_scheduled_change') ||
          (err.httpStatus === 409 && err.errorCode === 'subscription_schedule_already_terminated'))
      ) {
        window.setTimeout(() => {
          void refetch();
        }, 500);
        window.setTimeout(() => {
          void refetch();
        }, 2000);
        return;
      }
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.discardFailed);
      }
    } finally {
      setIsDiscarding(false);
    }
  };

  // Portal-redirect flow — retained for BrowseMode's onPortalUpdate (tier-card
  // deep-links: subscription-update / reactivate / cancel-downgrade). NOT the
  // banner discard CTA (that is handleDiscardScheduledChange above).
  const handleCancelPendingPlanChange = async () => {
    setActionError(null);
    try {
      const { url } = await createPortalSession('subscription_update');
      window.location.href = url;
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.portalSessionFailed);
      }
    }
  };

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div className="flex flex-col gap-5 py-5 md:gap-7 md:pt-10 md:pb-14 desktop:mx-auto desktop:max-w-[768px]">
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="flex items-center gap-1 self-start text-sm font-medium text-dream-navy hover:text-dream-navy/70"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            <span>{STRINGS.account.subscription.back}</span>
          </button>

          <h1 className="text-base font-semibold leading-4 text-dream-navy">
            {STRINGS.account.subscription.title}
          </h1>

          <SubscriptionSummaryCard
            state={state}
            onManageStripe={() => void handleManageStripe()}
            onReactivate={() => void handleReactivate()}
            isReactivating={isReactivating}
            onUpdatePaymentMethod={() => void handleUpdatePaymentMethod()}
            onCancelPendingPlanChange={() => void handleDiscardScheduledChange()}
            isDiscarding={isDiscarding}
            onScrollToBrowseMode={handleScrollToBrowseMode}
          />

          <ActivePackagesCard />

          {actionError && (
            <p role="alert" className="text-xs text-error" data-testid="subscription-action-error">
              {actionError}
            </p>
          )}

          <div ref={browseModeRef}>
            <BrowseMode
              subscriptionState={state}
              onPortalUpdate={() => void handleCancelPendingPlanChange()}
              onDiscardScheduledChange={() => void handleDiscardScheduledChange()}
            />
          </div>

          <PaketeSection />

          <WithdrawablePackages />

          {/* Cancel link — only rendered for active subscribers (PWA #43,
              Closing Commit 7). Trial users + already-pending-cancel users
              + users without a Stripe subscription don't see the link at
              all (vs the prior `disabled` state which still rendered the
              greyed-out element and confused trial users with an
              irrelevant cancel-affordance). */}
          {state && !state.cancelAtPeriodEnd && state.stripeSubscriptionId !== null && (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="self-center text-xs font-medium text-dream-navy/60 underline decoration-solid"
              data-testid="abo-kuendigen-link"
            >
              {STRINGS.account.subscription.cancelLink}
            </button>
          )}

          {/* §356a (L.3) — online withdrawal link, only when the subscription
              is within its withdrawal window (backend canWithdraw; forced
              false when the feature flag is off, so this stays hidden). */}
          {state && state.canWithdraw && state.subscriptionId && (
            <Link
              to={`/account/widerruf/subscription/${state.subscriptionId}`}
              className="self-center text-xs font-medium text-dream-navy/60 underline decoration-solid"
              data-testid="withdraw-subscription-link"
            >
              {STRINGS.account.withdrawal.button}
            </Link>
          )}
        </div>
      </ContentContainer>

      <CancelConfirmModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        endDate={formatGermanShortDate(state?.currentPeriodEnd ?? null)}
        onConfirm={() => void handleCancelConfirm()}
      />
    </div>
  );
}

// ── Route-private ActivePackagesCard (Sub-Phase E — S4 handling) ──

/**
 * S4 handling — additive card surfacing pack-credit reserves alongside
 * the active subscription.
 *
 * Pure data-driven from `useEntitlement().packRemaining`. Hidden when 0
 * (no card chrome at all — keeps S2-only layout identical for the
 * dominant case). Subscription stays the primary mental model
 * (use-it-or-lose-it monthly cycle in `SubscriptionSummaryCard`); the
 * pack card sits below as the "reserve buffer" view.
 *
 * Route-private — single consumer in this batch. Promote to
 * `pwa/src/components/account/ActivePackagesCard.tsx` if a 2nd surface
 * appears (FB-5 ≥2 rule).
 */
function ActivePackagesCard() {
  const { entitlement } = useEntitlement();
  const packRemaining = entitlement?.packRemaining ?? 0;
  const subscriptionRemaining = entitlement?.subscriptionRemaining ?? 0;
  const hasActiveSubscription =
    entitlement?.sources.some((s) => s.type === 'subscription') ?? false;
  // PWA #71 Sub-Phase D — three-case hide-logic:
  //   1. Sub>0 + Pack>0 (subscriber with pack reserve): show — this is
  //      the original Sub-Phase E behavior.
  //   2. Sub=0 + Pack>0 + active subscription exists (mixed-state, sub
  //      exhausted): hide — SummaryCard's reserve subline already
  //      surfaces pack info inline; this card would duplicate (D4 lock).
  //   3. No subscription + Pack>0 (pack-only user, never subscribed):
  //      show standalone — pack-only is a legitimate production user
  //      segment (users who prefer one-time package purchases over
  //      subscription commitments). SummaryCard returns null for them,
  //      so this card is the only A-02 surface for their pack count.
  //
  // Discriminator for "active subscription exists" via sources-array
  // matches pickReserveLine's pattern + the PWA #53 backend contract
  // (active subscription always emits a subscription source even at
  // 0 remaining).
  if (packRemaining <= 0) return null;
  if (subscriptionRemaining === 0 && hasActiveSubscription) return null;

  const S = STRINGS.account.subscription;
  const bodyText =
    packRemaining === 1
      ? S.activePackagesBodyOne
      : S.activePackagesBody.replace('{count}', String(packRemaining));

  return (
    <div
      className="flex flex-col items-start gap-1 rounded-lg bg-dream-white p-4"
      data-testid="active-packages-card"
    >
      <p
        className="text-sm font-semibold leading-[14px] text-dream-navy"
        data-testid="active-packages-body"
      >
        {bodyText}
      </p>
      <p className="text-xs text-dream-navy/60" data-testid="active-packages-subtext">
        {S.activePackagesSubtext}
      </p>
    </div>
  );
}

// ── Route-private SubscriptionSummaryCard ────────────────────

interface SubscriptionSummaryCardProps {
  state: SubscriptionState | null;
  onManageStripe: () => void;
  onReactivate: () => void;
  /** Bug PWA-A Bug-B: disables the reactivate CTA while a request is in flight. */
  isReactivating: boolean;
  onUpdatePaymentMethod: () => void;
  /** D1: now triggers the in-app discard-scheduled-change endpoint (was portal). */
  onCancelPendingPlanChange: () => void;
  /** D1: disables the "Plan-Wechsel rückgängig machen" CTA while in flight. */
  isDiscarding: boolean;
  /**
   * Subscription-Area Polish workstream Sub-Phase D — fired by the
   * "Jetzt weiter Geschichten erstellen" CTA when the subscriber has
   * burned their monthly quota (subscriptionRemaining === 0). Owner
   * (the route) smooth-scrolls to the BrowseMode tier-cards section.
   */
  onScrollToBrowseMode: () => void;
}

/**
 * Richer plan summary than PlanStatusCard — surfaces plan name, price,
 * billing interval, current period progress, usage line, and the
 * lifecycle highlight box when one of the 3 lifecycle phases is
 * active. Route-private (single consumer in Batch 9A — A-02).
 *
 * The internal warn/err highlight box duplicates the className pattern
 * used in PlanStatusCard's HighlightBox sub-component (Batch 9A
 * Commit 2). FLAGGED at Code-Review for potential extraction to a
 * shared `LifecycleHighlightBox` pattern when a 3rd consumer appears.
 */
function SubscriptionSummaryCard({
  state,
  onManageStripe,
  onReactivate,
  isReactivating,
  onUpdatePaymentMethod,
  onCancelPendingPlanChange,
  isDiscarding,
  onScrollToBrowseMode,
}: SubscriptionSummaryCardProps) {
  // PWA #71 — read entitlement to detect mixed-state (sub=0 + pack/trial>0).
  // Hook call placement matches the `ActivePackagesCard` precedent — the
  // route-private component owns its own data fetch, no prop drilling
  // from the route. Hook is safe to call before the early-return below.
  const { entitlement } = useEntitlement();

  if (!state || state.tierLabel === null) {
    // Trial / no-active-subscription state. Pre-Batch-11 Commit 2 this
    // returned a "Wähle deinen Plan" placeholder that called
    // createPortalSession() for a non-existent Stripe customer (D15).
    // Batch 11 swap: trial users now see the BrowseMode tier-cards
    // directly as their plan-selection surface (rendered below this
    // card by the route, unconditionally). Aktueller-Plan-Card is
    // suppressed in trial state since there is no active plan to
    // summarise.
    return null;
  }

  const lc = STRINGS.account.subscription.lifecycle;

  // Subscription-Area Polish workstream Sub-Phase D — counter line
  // leads with remaining stories (the prominent number) instead of
  // ambiguous "{used} von {total}".
  const subscriptionRemaining = Math.max(0, state.storiesPerMonth - state.storiesUsedThisMonth);
  const isOutOfCredits = subscriptionRemaining === 0;
  const usageText = STRINGS.account.subscription.usage
    .replace('{remaining}', String(subscriptionRemaining))
    .replace('{total}', String(state.storiesPerMonth));

  // PWA #71 — mixed-state subline. When the active subscription is
  // exhausted (sub=0) but pack/trial credits give the user creation
  // capacity, replace the "Noch 0 von 5" usageText with a depletion line
  // and surface the reserve below it. pickReserveLine returns null in
  // every other state (active sub with credits, or all sources empty)
  // so the existing usageText path stays for those cases.
  const reserve = pickReserveLine(entitlement);

  // Refresh-line — when the next batch arrives. Sourced from
  // currentPeriodEnd (always +1 month per Backend #39 doctrine).
  const refreshLineText = STRINGS.account.subscription.refreshLine
    .replace('{count}', String(state.storiesPerMonth))
    .replace('{date}', formatGermanShortDate(state.currentPeriodEnd));

  // Renewal-line — when Stripe charges next. Annual subs use
  // stripeBillingCycleEnd (≈12 months out); monthly subs use
  // currentPeriodEnd (same date as refresh, but rendered anyway for
  // cross-tier consistency per Sebastian Sub-Phase D lock).
  // Defensive null-handling: pre-backfill rows have stripeBillingCycleEnd
  // null even on annual subs — fall back to currentPeriodEnd in that
  // case so the line never renders "—" in production.
  const renewalDateSource =
    state.billingInterval === 'annual'
      ? (state.stripeBillingCycleEnd ?? state.currentPeriodEnd)
      : state.currentPeriodEnd;
  const renewalLineText = STRINGS.account.subscription.renewalLine.replace(
    '{date}',
    formatGermanShortDate(renewalDateSource),
  );

  const billingLabel = state.billingInterval === 'annual' ? 'jährlich' : 'monatlich';
  const priceLine = formatPriceLine(state, billingLabel);

  const isPendingCancel = state.cancelAtPeriodEnd;
  const isPastDue = state.isPastDue;
  const isPendingPlanChange = !isPastDue && !isPendingCancel && Boolean(state.pendingDowngradeTier);

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-4 rounded-lg p-4',
        isPastDue ? 'bg-dream-white' : 'bg-dream-white',
      )}
      data-testid="subscription-summary-card"
    >
      <div className="flex w-full flex-col items-start gap-2">
        <p className="text-sm font-semibold leading-[14px] text-dream-navy">
          {STRINGS.account.subscription.aktuellerPlan}
        </p>
        <p className="text-xs text-dream-navy opacity-70">{priceLine}</p>
      </div>

      {/* Counter row + (out-of-credits CTA) + dual date lines —
          hidden in Past-Due state since the renewal date is no longer
          trustworthy until payment recovers. */}
      {!isPastDue && (
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col gap-1">
            <div className="h-[2px] w-full rounded-full bg-dream-navy/10">
              {/* Counter-bar fill: gold while credits remain; subtle navy
                  desaturation at 0 to signal depletion without alarming. */}
              <div
                className={cn(
                  'h-full rounded-full',
                  isOutOfCredits ? 'bg-dream-navy/30' : 'bg-dream-gold',
                )}
                style={{
                  width: `${progressPct(state.storiesUsedThisMonth, state.storiesPerMonth)}%`,
                }}
                data-testid="subscription-counter-bar-fill"
              />
            </div>
            {reserve ? (
              <div className="flex w-full flex-col gap-1 text-xs text-dream-navy/70">
                <p data-testid="subscription-depletion-line">{reserve.depletionLine}</p>
                <p data-testid="subscription-reserve-line">{reserve.reserveLine}</p>
              </div>
            ) : (
              <p className="text-xs text-dream-navy/70" data-testid="subscription-counter-text">
                {usageText}
              </p>
            )}
          </div>

          {/* Out-of-credits CTA — rendered between counter row and date
              rows when subscriptionRemaining === 0. Smooth-scrolls to
              the BrowseMode tier-cards below the card. */}
          {isOutOfCredits && (
            <button
              type="button"
              onClick={onScrollToBrowseMode}
              className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy transition-colors hover:bg-dream-gold/90"
              data-testid="subscription-cta-weiter-erstellen"
            >
              {STRINGS.account.subscription.ctaWeiterErstellen}
            </button>
          )}

          <div className="flex w-full flex-col gap-1 text-xs text-dream-navy/60">
            <p data-testid="subscription-refresh-line">{refreshLineText}</p>
            <p data-testid="subscription-renewal-line">{renewalLineText}</p>
          </div>
        </div>
      )}

      {/* Lifecycle highlight box — only one of the three can render at a time. */}
      {isPastDue && (
        <LifecycleHighlightBox tone="error" testId="subscription-highlight-past-due">
          <span className="font-medium">{lc.pastDue.highlightPrefix}</span>
          <br />
          {lc.pastDue.highlightBody}
        </LifecycleHighlightBox>
      )}

      {isPendingCancel && (
        <LifecycleHighlightBox tone="warning" testId="subscription-highlight-pending-cancel">
          <span className="font-medium">
            {lc.pendingCancel.highlightPrefix.replace(
              '{date}',
              formatGermanShortDate(state.endsAt ?? state.currentPeriodEnd),
            )}
          </span>
          <br />
          {lc.pendingCancel.highlightBody}
        </LifecycleHighlightBox>
      )}

      {isPendingPlanChange && (
        <LifecycleHighlightBox tone="warning" testId="subscription-highlight-pending-plan-change">
          <span className="font-medium">
            {lc.pendingPlanChange.highlightPrefix.replace(
              '{date}',
              formatGermanShortDate(state.pendingDowngradeAt),
            )}
          </span>
          <br />
          {lc.pendingPlanChange.highlightBody
            .replace('{newPlanName}', state.pendingDowngradeTierLabel ?? '—')
            .replace('{currentPlanName}', state.tierLabel ?? '—')}
        </LifecycleHighlightBox>
      )}

      {/* Primary CTA — varies by lifecycle state */}
      {isPastDue ? (
        <button
          type="button"
          onClick={onUpdatePaymentMethod}
          className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-error px-4 py-[14px] text-xs font-semibold leading-3 text-dream-white transition-colors hover:bg-error/90"
          data-testid="subscription-cta-update-payment"
        >
          {lc.pastDue.ctaLabel}
        </button>
      ) : isPendingCancel ? (
        <button
          type="button"
          onClick={onReactivate}
          disabled={isReactivating}
          className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-success px-4 py-[14px] text-xs font-semibold leading-3 text-dream-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="subscription-cta-reactivate"
        >
          {lc.pendingCancel.ctaLabel}
        </button>
      ) : isPendingPlanChange ? (
        <button
          type="button"
          onClick={onCancelPendingPlanChange}
          disabled={isDiscarding}
          className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-success px-4 py-[14px] text-xs font-semibold leading-3 text-dream-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="subscription-cta-cancel-plan-change"
        >
          {lc.pendingPlanChange.ctaLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={onManageStripe}
          className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy transition-colors hover:bg-dream-gold/90"
          data-testid="subscription-cta-manage-stripe"
        >
          {STRINGS.account.subscription.manageStripeCta}
        </button>
      )}
    </div>
  );
}

// ── Route-private BrowseMode + TierCard ──────────────────────

/**
 * BrowseMode — A-02 plan-selection surface (Batch 11 Commits 2 + 3).
 *
 * Renders the SectionHeading, Yearly/Monthly toggle, and 3 TierCards
 * (Entdecker / Abenteurer / Geschichten-Profi). State-agnostic: shown
 * for trial users (D15: replaces the prior portal-session placeholder)
 * AND for active subscribers (browse to upgrade / downgrade). Always
 * mounted unconditionally below the SubscriptionSummaryCard.
 *
 * Toggle defaults to `'yearly'` per D10 — no cross-session persistence.
 *
 * Commit 3 wires the 6-state CTA matrix per Briefing K-A-02 via
 * `deriveTierCardState`: trial / current / upgrade / downgrade /
 * pending-cancel / pending-downgrade-target-undercut. The CTA action
 * dispatches one of three click-handlers:
 *   - 'choose-plan'                     → /checkout/review?plan={planSlug}
 *   - 'reactivate-deep-link' /          → onPortalUpdate() (Stripe
 *     'cancel-downgrade-deep-link'        Customer Portal flow=subscription_update;
 *                                          one Portal flow handles both
 *                                          reactivate-from-cancel and
 *                                          undo-downgrade server-side).
 *   - 'no-op'                           → disabled, no fire.
 *
 * Route-private. FB-5 promote-candidate when a 2nd consumer surfaces
 * (most likely a Phase 6+ logged-out Pricing-Page; until then the
 * shape stays here).
 */
interface BrowseModeProps {
  subscriptionState: SubscriptionState | null;
  /** Wired to handleCancelPendingPlanChange in the parent — POSTs
   *  /api/subscriptions/portal-session with flow='subscription_update'
   *  for active-state upgrade/downgrade/cycle-switch. */
  onPortalUpdate: () => void;
  /** PWA-B: wired to handleDiscardScheduledChange in the parent — the in-app
   *  POST /api/subscriptions/discard-scheduled-change for the
   *  'cancel-downgrade-in-app' tier-card action. */
  onDiscardScheduledChange: () => void;
}

function BrowseMode({
  subscriptionState,
  onPortalUpdate,
  onDiscardScheduledChange,
}: BrowseModeProps) {
  const navigate = useNavigate();
  const { plans, isLoading, error } = useSubscriptionPlans();
  const [interval, setInterval] = useState<BrowseInterval>('yearly');

  const subscriptionPlans = (plans ?? []).filter(
    (p) => p.mode === 'subscription' && p.billingInterval === interval,
  );
  const tierOrder: Array<{ tierKey: string; name: string }> = [
    { tierKey: 'entdecker', name: STRINGS.account.subscription.browseMode.tier.entdeckerName },
    { tierKey: 'abenteurer', name: STRINGS.account.subscription.browseMode.tier.abenteurerName },
    {
      tierKey: 'geschichten_profi',
      name: STRINGS.account.subscription.browseMode.tier.profiName,
    },
  ];

  return (
    <section className="flex w-full flex-col gap-3" data-testid="subscription-browse-mode">
      <h2 className="text-sm font-semibold leading-[14px] text-dream-navy">
        {STRINGS.account.subscription.browseMode.heading}
      </h2>

      <div
        role="group"
        aria-label={STRINGS.account.subscription.browseMode.heading}
        className="inline-flex self-start rounded-full border border-dream-navy/15 p-0.5 text-xs"
        data-testid="browse-mode-toggle"
      >
        <button
          type="button"
          onClick={() => setInterval('yearly')}
          aria-pressed={interval === 'yearly'}
          data-testid="browse-mode-toggle-yearly"
          className={cn(
            'rounded-full px-3 py-1.5 font-medium transition-colors',
            interval === 'yearly'
              ? 'bg-dream-navy text-dream-white'
              : 'text-dream-navy/70 hover:text-dream-navy',
          )}
        >
          {STRINGS.account.subscription.browseMode.toggleYearly}
        </button>
        <button
          type="button"
          onClick={() => setInterval('monthly')}
          aria-pressed={interval === 'monthly'}
          data-testid="browse-mode-toggle-monthly"
          className={cn(
            'rounded-full px-3 py-1.5 font-medium transition-colors',
            interval === 'monthly'
              ? 'bg-dream-navy text-dream-white'
              : 'text-dream-navy/70 hover:text-dream-navy',
          )}
        >
          {STRINGS.account.subscription.browseMode.toggleMonthly}
        </button>
      </div>

      {interval === 'yearly' && (
        <span
          className="self-start rounded-full bg-dream-gold/20 px-2 py-0.5 text-xxs font-semibold leading-none text-dream-navy"
          data-testid="browse-mode-yearly-hint"
        >
          {STRINGS.account.subscription.browseMode.toggleYearlyHint}
        </span>
      )}

      {error && !isLoading && (
        <p role="alert" className="text-xs text-error" data-testid="browse-mode-error">
          {STRINGS.account.subscription.browseMode.errors.loadFailed}
        </p>
      )}

      {/* PWA-B: while a pending change is open, the tier cards are
          non-actionable (disabled) — this header explains why and points the
          user to the in-app resolve action first. */}
      {subscriptionState?.cancelAtPeriodEnd ? (
        <p className="text-xs text-dream-navy/70" data-testid="browse-mode-pending-header">
          {STRINGS.account.subscription.browseMode.pendingCancelHeader}
        </p>
      ) : subscriptionState?.pendingDowngradeTier ? (
        <p className="text-xs text-dream-navy/70" data-testid="browse-mode-pending-header">
          {STRINGS.account.subscription.browseMode.pendingDowngradeHeader.replace(
            '{newPlanName}',
            subscriptionState.pendingDowngradeTierLabel ?? '',
          )}
        </p>
      ) : null}

      <div className="flex w-full flex-col gap-2">
        {tierOrder.map(({ tierKey, name }) => {
          const plan = subscriptionPlans.find((p) => p.tierKey === tierKey);
          const cardState = deriveTierCardState({
            subscriptionState,
            cardTierKey: tierKey,
            cardInterval: interval,
          });
          return (
            <TierCard
              key={tierKey}
              tierKey={tierKey}
              displayName={name}
              plan={plan}
              interval={interval}
              isLoading={isLoading}
              cardState={cardState}
              onClick={() => {
                if (cardState.action === 'choose-plan' && plan) {
                  navigate(`/checkout/review?plan=${plan.planKey}`);
                } else if (cardState.action === 'cancel-downgrade-in-app') {
                  // PWA-B: the downgrade-target card discards the scheduled
                  // change via the in-app C2 endpoint (NOT the Stripe Portal).
                  onDiscardScheduledChange();
                } else if (cardState.action === 'subscription-update-deep-link') {
                  // Active-sub upgrade / downgrade / cycle-switch route through
                  // the Stripe Customer Portal subscription_update flow.
                  onPortalUpdate();
                }
                // 'no-op' (disabled cards) → no fire. The legacy
                // 'reactivate-deep-link' / 'cancel-downgrade-deep-link' actions
                // are no longer produced post-PWA-B (marked for removal).
              }}
            />
          );
        })}
      </div>
    </section>
  );
}

interface TierCardProps {
  tierKey: string;
  displayName: string;
  plan: PublicPlan | undefined;
  interval: BrowseInterval;
  isLoading: boolean;
  cardState: TierCardState;
  onClick: () => void;
}

/**
 * Single tier-card row inside BrowseMode. State-matrix-driven CTA per
 * Briefing K-A-02 (Batch 11 Commit 3): renders a green "success"
 * variant for reactivate / cancel-downgrade flows, the gold "primary"
 * variant for upgrade / downgrade / trial / choose-plan, and a grey
 * "Dein aktueller Plan" badge in lieu of the CTA when the card matches
 * the user's active subscription.
 *
 * Route-private; promote-candidate per BrowseMode JSDoc.
 */
function TierCard({
  tierKey,
  displayName,
  plan,
  interval,
  isLoading,
  cardState,
  onClick,
}: TierCardProps) {
  const browseS = STRINGS.account.subscription.browseMode;
  const priceTemplate =
    interval === 'yearly' ? browseS.tier.priceLineYearly : browseS.tier.priceLineMonthly;

  // Yearly cards: backend `amountEuros` is the FULL annual charge
  // (e.g. 47.88 for entdecker-jaehrlich). The price-line displays
  // monthly-equivalent (~3.99) per Briefing K-A-02 — divide by 12.
  // Two-decimal German formatting (",").
  const monthlyEquivalent = plan
    ? interval === 'yearly'
      ? plan.amountEuros / 12
      : plan.amountEuros
    : null;
  const priceLine =
    monthlyEquivalent !== null
      ? priceTemplate.replace('{price}', formatEuroAmount(monthlyEquivalent))
      : null;

  const storiesLine = plan?.storiesPerMonth
    ? browseS.tier.storiesPerMonth.replace('{count}', String(plan.storiesPerMonth))
    : null;

  // Variant → CSS class. Mirrors the existing PlanStatusCard ctaVariants
  // pattern (gold primary / green success / disabled grey).
  const ctaClass =
    cardState.ctaVariant === 'success'
      ? 'bg-success text-dream-white hover:bg-success/90'
      : 'bg-dream-gold text-dream-navy hover:bg-dream-gold/90';

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-dream-navy/10 bg-dream-white p-4"
      data-testid={`tier-card-${tierKey}`}
    >
      <p className="text-sm font-semibold leading-[14px] text-dream-navy">{displayName}</p>
      {priceLine && (
        <p className="text-xs text-dream-navy/70" data-testid={`tier-card-${tierKey}-price`}>
          {priceLine}
        </p>
      )}
      {storiesLine && <p className="text-xs text-dream-navy/60">{storiesLine}</p>}
      {cardState.disabled && cardState.badgeLabel ? (
        <button
          type="button"
          disabled
          className="mt-2 flex w-full items-center justify-center rounded-lg bg-dream-navy/10 px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy/60"
          data-testid={`tier-card-${tierKey}-badge`}
          aria-label={cardState.badgeLabel}
        >
          {cardState.badgeLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={onClick}
          // PWA-B: cardState.disabled (non-current pending cards, no badge)
          // greys the button out and blocks the click — the grid header above
          // explains why; the user resolves the pending change first.
          disabled={isLoading || !plan || cardState.disabled}
          className={cn(
            'mt-2 flex w-full items-center justify-center overflow-hidden rounded-lg px-4 py-[14px] text-xs font-semibold leading-3 transition-colors disabled:opacity-50',
            ctaClass,
          )}
          data-testid={`tier-card-${tierKey}-cta`}
        >
          {cardState.ctaLabel}
        </button>
      )}
    </div>
  );
}

function formatEuroAmount(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

// ── Route-private PaketeSection + PackCard (Batch 11 Commit 4) ──

/**
 * Einmalpakete (one-time package) section. State-agnostic per
 * Briefing K-A-02 line 332: always rendered, regardless of
 * subscription state (trial / active / pending-cancel / past-due /
 * pending-downgrade). CTA is a flat "Kaufen" — no state-matrix.
 *
 * Pack discriminator: `plan.mode === 'payment'` (Risk #3 from
 * Commit 0 code-review — `tierKey` is empty-string for packs, NOT
 * null). Catalog source-file order produces pack-10 first, then
 * pack-25 with the "-20%"-Badge.
 *
 * Route-private. FB-5 promote-candidate when a 2nd consumer surfaces
 * (Phase 6+ Pricing-Page logged-out marketing surface).
 */
function PaketeSection() {
  const navigate = useNavigate();
  const { plans, isLoading } = useSubscriptionPlans();

  const packPlans = (plans ?? []).filter((p) => p.mode === 'payment');

  return (
    <section className="flex w-full flex-col gap-3" data-testid="subscription-pakete-section">
      <h2 className="text-sm font-semibold leading-[14px] text-dream-navy">
        {STRINGS.account.subscription.packs.heading}
      </h2>

      <div className="flex w-full flex-col gap-2">
        {packPlans.map((plan) => (
          <PackCard
            key={plan.planKey}
            plan={plan}
            isLoading={isLoading}
            // The "-20%"-Badge appears on pack-25 only per Briefing
            // K-A-02 line 334. Discriminator is the planKey suffix —
            // robust to catalog reorderings.
            showDiscountBadge={plan.planKey === 'pack-25'}
            onClick={() => navigate(`/checkout/review?plan=${plan.planKey}`)}
          />
        ))}
      </div>
    </section>
  );
}

interface PackCardProps {
  plan: PublicPlan;
  isLoading: boolean;
  showDiscountBadge: boolean;
  onClick: () => void;
}

function PackCard({ plan, isLoading, showDiscountBadge, onClick }: PackCardProps) {
  const packsS = STRINGS.account.subscription.packs;

  const titleLine = packsS.title.replace('{count}', String(plan.storiesGranted ?? 0));
  const priceLine = packsS.priceLine.replace('{amount}', formatEuroAmount(plan.amountEuros));

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-dream-navy/10 bg-dream-white p-4"
      data-testid={`pack-card-${plan.planKey}`}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-[14px] text-dream-navy">{titleLine}</p>
        {showDiscountBadge && (
          <span
            className="shrink-0 rounded-full bg-dream-gold/20 px-2 py-0.5 text-xxs font-semibold leading-none text-dream-navy"
            data-testid={`pack-card-${plan.planKey}-discount-badge`}
          >
            {packsS.discountHint}
          </span>
        )}
      </div>
      <p className="text-xs text-dream-navy/70" data-testid={`pack-card-${plan.planKey}-price`}>
        {priceLine}
      </p>
      <button
        type="button"
        onClick={onClick}
        disabled={isLoading}
        className="mt-2 flex w-full items-center justify-center overflow-hidden rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy transition-colors hover:bg-dream-gold/90 disabled:opacity-50"
        data-testid={`pack-card-${plan.planKey}-cta`}
      >
        {packsS.cta}
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────

// LifecycleHighlightBox extracted to `patterns/LifecycleHighlightBox.tsx`
// in Commit 3a (2nd consumer crossed FB-5 ≥2 rule). The previous
// route-private implementation here used `testIdSuffix` to compose
// the testid; the shared pattern takes a raw `testId` and the route
// composes `subscription-highlight-{phase}` at the call-site to keep
// existing test selectors stable.

function formatPriceLine(state: SubscriptionState, billingLabel: string): string {
  if (state.amountCents === null) {
    return `${state.tierLabel ?? '—'} (${billingLabel})`;
  }
  const euros = (state.amountCents / 100).toFixed(2).replace('.', ',');
  return `${state.tierLabel} (${billingLabel}) · ${euros} EUR/Monat`;
}

function progressPct(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
}

function formatGermanShortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

// ── Route-private WithdrawablePackages (§356a L.3) ────────────────
/**
 * Per-eligible-package "Vertrag widerrufen" button. The aggregate pack card
 * (ActivePackagesCard / PaketeSection) has no per-contract identity, so the
 * §356a button for packages renders here from the withdrawable-contracts
 * endpoint. Hidden entirely when no package is withdrawable (incl. flag off,
 * where the backend returns an empty array).
 */
function WithdrawablePackages() {
  const { contracts } = useWithdrawableContracts();
  const packages = contracts.filter((c) => c.contractType === 'package' && c.canWithdraw);
  if (packages.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {packages.map((pkg) => (
        <div
          key={pkg.contractId}
          className="flex items-center justify-between gap-3 rounded-lg bg-dream-white p-4"
          data-testid="withdrawable-package"
        >
          <span className="text-sm text-dream-navy">{pkg.label}</span>
          <Link
            to={`/account/widerruf/package/${pkg.contractId}`}
            className="shrink-0 text-xs font-medium text-dream-navy/60 underline decoration-solid"
            data-testid="withdraw-package-link"
          >
            {STRINGS.account.withdrawal.button}
          </Link>
        </div>
      ))}
    </div>
  );
}
